import { useMemo, useEffect, useRef } from 'react';
import { t } from '@lingui/macro';
import { getCountries } from 'country-state-picker';
import { useFormContext } from 'react-hook-form';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import { CountryCode } from 'libphonenumber-js';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import TextField from '@decisiv/ui-components/lib/components/TextField';
import { P, H4 } from '@decisiv/ui-components/lib/components/Typography';
import AsteriskIcon from '@decisiv/iconix/lib/components/Asterisk';

import {
  TextField as FormTextField,
  Select,
  PhoneTextField,
} from 'components/Form';
import {
  US_STATES,
  CANADA_STATES,
  ECUADOR_STATES,
  MEXICO_STATES,
  CHILE_STATES,
  JAPAN_STATES,
  isStateValidForCountry,
} from 'utils/countryRegions';
import { codeToCountry } from 'utils/codeToCountry';
import { DIAL_CODE_OPTIONS } from 'pages/Management/constants';

interface ContentProps {
  platformAccountId: string;
}

const ALLOWED_COUNTRIES = ['US', 'CA', 'PR', 'EC', 'MX', 'CL', 'JP'];

const COUNTRY_OPTIONS = getCountries()
  .filter(({ code }: { code: string }) =>
    ALLOWED_COUNTRIES.includes(code.toUpperCase()),
  )
  .map(({ code, name }: { code: string; name: string }) => ({
    id: code.toUpperCase(),
    label: name,
    value: code.toUpperCase(),
  }));

const Content = ({ platformAccountId }: ContentProps) => {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');
  const stateValue = watch('state');
  const countryCodeValue = watch('countryCode');
  const previousCountryRef = useRef(countryValue);

  const allStateOptions = useMemo(() => {
    const countryGroups = [
      { country: 'United States', states: US_STATES },
      { country: 'Canada', states: CANADA_STATES },
      { country: 'Mexico', states: MEXICO_STATES },
      { country: 'Chile', states: CHILE_STATES },
      { country: 'Ecuador', states: ECUADOR_STATES },
      { country: 'Japan', states: JAPAN_STATES },
    ];

    return countryGroups.flatMap(({ country, states }) => [
      {
        id: `header-${country}`,
        label: country,
        value: `header-${country}`,
        disabled: true,
        style: { fontWeight: 'bold', backgroundColor: '#f5f5f5' },
      },
      ...states.map(({ code, name }) => ({
        id: code,
        label: `  ${name}`,
        value: code,
      })),
    ]);
  }, []);

  const invalidStateSelected =
    !!countryValue &&
    !!stateValue &&
    !isStateValidForCountry(stateValue, countryValue);

  useEffect(() => {
    const previousCountry = previousCountryRef.current;

    /* istanbul ignore next -- @preserve */
    if (previousCountry && previousCountry !== countryValue) {
      setValue('state', '', { shouldValidate: true });
    }
    previousCountryRef.current = countryValue;
  }, [countryValue, setValue]);

  return (
    <Flex flexDirection="column" marginTop={1}>
      <Flex justifyContent="flex-end" marginBottom={1}>
        <P size="small" shade={1}>
          <AsteriskIcon
            size="medium"
            color={toColorString(color.status.information.medium)}
          />{' '}
          {t`Required Fields`}
        </P>
      </Flex>

      <Flex marginBottom={1}>
        <TextField value={platformAccountId} label={t`ID`} readOnly />
      </Flex>

      <Flex marginBottom={1}>
        <FormTextField
          name="name"
          label={t`Name`}
          placeholder={t`Enter fleet account name`}
          required
        />
      </Flex>

      <Flex marginBottom={1}>
        <FormTextField
          name="address1"
          label={t`Address Line 1`}
          placeholder={t`Enter address line 1`}
          required
        />
      </Flex>

      <Flex marginBottom={1}>
        <FormTextField
          name="address2"
          label={t`Address Line 2`}
          placeholder={t`Enter address line 2`}
        />
      </Flex>

      <Flex marginBottom={1}>
        <Flex flex={1}>
          <FormTextField
            name="city"
            label={t`City`}
            placeholder={t`Enter city`}
            required
          />
        </Flex>

        <Flex flex={1} marginLeft={1}>
          <Select
            name="state"
            label={t`State`}
            options={allStateOptions}
            required
            clearButtonLabel={t`Clear State`}
            defaultValue=""
          />
        </Flex>
      </Flex>

      {invalidStateSelected && (
        <Flex marginTop={-0.5} marginBottom={1}>
          <P size="small" shade={2}>
            {t`Select a state that belongs to the chosen country.`}
          </P>
        </Flex>
      )}

      <Flex marginBottom={1}>
        <Flex flex={1}>
          <FormTextField
            name="postalCode"
            label={t`Postal Code`}
            placeholder={t`Enter postal code`}
            required
          />
        </Flex>

        <Flex flex={1} marginLeft={1}>
          <Select
            name="country"
            label={t`Country`}
            options={COUNTRY_OPTIONS}
            required
            defaultValue=""
          />
        </Flex>
      </Flex>

      <Flex marginTop={2}>
        <H4>
          {t`Contact Information`}{' '}
          <AsteriskIcon
            size="medium"
            color={toColorString(color.status.information.medium)}
          />
        </H4>
      </Flex>

      <Flex marginBottom={1}>
        <P size="small" shade={1}>
          {t`Provide at least an email address or phone number.`}
        </P>
      </Flex>

      <Flex marginBottom={1}>
        <Flex flex={0} marginRight={1} style={{ minWidth: '120px' }}>
          <Select
            name="countryCode"
            label={t`Country Code`}
            options={DIAL_CODE_OPTIONS}
            defaultValue="+1"
          />
        </Flex>
        <Flex flex={1}>
          <PhoneTextField
            name="phoneNumber"
            label={t`Phone Number`}
            countryCode={codeToCountry(countryCodeValue) as CountryCode}
          />
        </Flex>
      </Flex>

      <Flex>
        <FormTextField name="email" label={t`Email`} type="email" />
      </Flex>
    </Flex>
  );
};

export default Content;
